import { Component, Input, inject, Inject, LOCALE_ID } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { WidgetsModule } from '../../../../../../../shared/widgets.module';
// Tier 2.4: WjGridModule + CollectionView removed — replaced by AG Grid via
// KiteGridComponent (for Available + View grids) and ag-grid-angular direct
// (for the Configuration grid that embeds an interactive Angular widget —
// DateSelectorComponent — which can't easily flow through KiteGridComponent's
// pre-registered frameworkComponents map without coupling the wrapper to a
// specific shared component).
import { AgGridModule } from 'ag-grid-angular';
import { BenefitRulesService } from '../../../../../../../services/benefit-rules/benefit-rules.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, signal } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { DateSelectorComponent } from '../../../../../../../shared/widgets/date-selector/date-selector.component';

@Component({
    selector: 'app-assigned-benefits-modal',
    imports: [CommonModule, WidgetsModule, AgGridModule
        // DateSelectorComponent is NOT listed here to suppress NG8113 — it's
        // registered into AG Grid's frameworkComponents (see `configurationGridOptions`
        // in the class body) by class reference. The Angular template parser doesn't
        // see it because it's instantiated via AG Grid's framework registry.
    ],
    templateUrl: './assigned-benefits-modal.component.html'
})
export class AssignedBenefitsModalComponent {
  public readonly activeModal = inject(NgbActiveModal);
  private readonly benefitService = inject(BenefitRulesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastr = inject(ToastrService);

  @Input() ecoBenefits: any[] = [];
  @Input() trapQuantity: number = 0;
  @Input() capacity: number = 0;
  @Input() restaurantCount: number = 1;

  public isEditMode = signal(false);
  public availableBenefits = signal<any[]>([]);
  public loadingAvailable = signal(false);
  public errorMessage = signal('');

  /** Configuration grid — used directly with `<ag-grid-angular>` (not via wrapper)
   *  because it embeds an interactive DateSelectorComponent via frameworkComponents. */
  configurationGridOptions: any = {};
  configurationColumnDefs: any[] = [];

  /** Available grid options — enables checkbox row selection so the user can
   *  tick multiple benefits in one go (per the original `<app-checkbox>` UX). */
  availableGridOptions: any = {
    rowSelection: { mode: 'multiRow', checkboxes: true, headerCheckbox: true },
    suppressRowClickSelection: true,
    enableClickSelection: false
  };

  /** View grid options (read-only summary). */
  viewGridOptions: any = {};

  public durationMonthOptions: Array<{code: number, name: string}> = [
    { code: 1, name: '1 Month' },
    { code: 2, name: '2 Months' },
    { code: 3, name: '3 Months' },
    { code: 6, name: '6 Months' },
    { code: 9, name: '9 Months' },
    { code: 12, name: '1 Year' },
    { code: 24, name: '2 Years' },
    { code: 36, name: '3 Years' }
  ];

  /** Available grid column defs — single "Service" col + AG Grid built-in checkbox
   *  in the row header (driven by `rowSelection` config on `availableGridOptions`). */
  availableColumnDefs: any[] = [];

  /** View grid column defs — read-only summary. */
  viewColumnDefs: any[] = [];

  constructor(@Inject(LOCALE_ID) private locale: string) {
    this.availableColumnDefs = [
      { field: 'benefit_service.name', headerName: 'Service', flex: 1, cellClass: 'ag-cell-left' }
    ];

    // View grid — read-only display.
    this.viewColumnDefs = [
      { field: 'service_name', headerName: 'Service', flex: 2, cellClass: 'ag-cell-left' },
      { field: 'quantity', headerName: 'Quantity', width: 90, cellClass: 'ag-cell-center' },
      { field: 'duration_months', headerName: 'Duration', width: 150, cellClass: 'ag-cell-center',
        valueFormatter: (p: any) => p.value ? this.formatDuration(p.value) : '—' },
      { field: 'when_dates', headerName: 'Scheduled Dates', flex: 3, cellClass: 'ag-cell-left',
        valueFormatter: (p: any) => {
          if (!p.value || p.value.length === 0) return 'Any Time';
          return this.formatDates(p.value);
        } }
    ];

    // Configuration grid — inline editing with quantity/duration edits + an
    // embedded DateSelectorComponent for picking dates. Uses ag-grid-angular directly
    // (not the wrapper) because we need to register DateSelectorComponent as a
    // frameworkComponent locally.
    this.configurationColumnDefs = [
      { field: 'benefit_service.name', headerName: 'Service', width: 180, cellClass: 'ag-cell-left' },
      { field: 'quantity', headerName: 'Quantity', width: 90, cellClass: 'ag-cell-center',
        editable: true, cellEditor: 'agNumberCellEditor', cellEditorParams: { min: 1, precision: 0 } },
      { field: 'duration_months', headerName: 'Period', width: 130, cellClass: 'ag-cell-center',
        editable: true,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: this.durationMonthOptions.map(o => o.code) } },
      {
        headerName: 'When', flex: 1, cellClass: 'ag-cell-left',
        // Use the DateSelectorComponent directly as a cell renderer (interactive).
        // Selection is wired via `(selectionFinished)` on the component itself,
        // but AG Grid requires the renderer instance to be re-created for params
        // updates. The component below uses NgModel to write back to `when_dates`.
        cellRenderer: DateSelectorComponent,
        cellRendererParams: {
          iconMode: (params: any) => params.data?.when_dates?.length > 0,
          maxDates: (params: any) => params.data?.quantity,
          minDate: () => this.getMinDate(),
          maxDate: (params: any) => this.getMaxDate(params.data),
          initialDates: (params: any) => params.data?.when_dates ?? [],
          enableTime: true,
          initialMode: (params: any) => (params.data?.when_dates?.length ?? 0) > 0 ? 'scheduled' : 'any_time',
          selectionFinished: (event: any) => {
            const data = this.findRowById(event.data?._id || this.selectionTargetId);
            if (!data) return;
            if (event.mode === 'any_time') {
              data.when_dates = [];
            } else {
              data.when_dates = [...event.dates];
            }
          }
        }
      }
    ];

    this.configurationGridOptions = {
      frameworkComponents: {
        dateSelectorRenderer: DateSelectorComponent
      },
      defaultColDef: {
        resizable: true,
        sortable: false
      },
      onCellValueChanged: (event: any) => {
        if (event.colDef?.field === 'quantity') {
          const item = event.data;
          this.onQuantityChange(item, Number(event.newValue));
        } else if (event.colDef?.field === 'duration_months') {
          const item = event.data;
          this.onDurationChange(item, Number(event.newValue));
        }
      }
    };
  }

  /** Cached id of the row currently rendering the DateSelector cell.
   *  Cell renderers can't capture event data directly — we use a getter that
   *  matches by stable id during the (selectionFinished) callback. */
  selectionTargetId: string | null = null;

  private findRowById(id: string): any {
    if (!id) return null;
    return this.availableBenefits().find(b => b._id === id) ?? null;
  }

  formatDates(dates: Date[] | string[]): string {
    if (!dates || dates.length === 0) return '';
    return dates.map(d => formatDate(d, 'MMM d y h:mm a', this.locale)).join(', ');
  }

  formatDuration(months: number | string | null | undefined): string {
    if (!months) return '—';
    const m = Number(months);
    if (isNaN(m)) return String(months);

    if (m === 0) return '0 Months';

    const years = Math.floor(m / 12);
    const remainingMonths = m % 12;

    const parts: string[] = [];
    if (years > 0) {
      parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
    }
    if (remainingMonths > 0) {
      parts.push(`${remainingMonths} ${remainingMonths === 1 ? 'Month' : 'Months'}`);
    }

    return parts.join(' and ');
  }

  toggleEdit(): void {
    if (this.isEditMode()) {
      this.isEditMode.set(false);
    } else {
      this.fetchAvailableBenefits();
      this.isEditMode.set(true);
    }
  }

  fetchAvailableBenefits(): void {
    this.loadingAvailable.set(true);
    this.errorMessage.set('');
    this.benefitService.checkClientBenefits({
      trap_quantity: this.trapQuantity,
      capacity: this.capacity,
      restaurant_count: this.restaurantCount
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (res: any) => {
        if (res.ok) {
          const processedBenefits = (res.qualifiedBenefits || []).map((benefit: any) => {
            const existing = this.getExistingBenefit(benefit._id || '');
            return {
              ...benefit,
              service_id: benefit.benefit_service?._id || existing.service_id,
              service_name: benefit.benefit_service?.name || existing.service_name,
              selected: !!existing,
              quantity: existing?.quantity || 1,
              duration_months: existing?.duration_months || 1,
              when_dates: existing?.when_dates || []
            };
          });
          this.availableBenefits.set(processedBenefits);
        } else {
          this.errorMessage.set(res.msg || 'Error loading benefits');
        }
        this.loadingAvailable.set(false);
      },
      error: (err: any) => {
        this.errorMessage.set(err?.error?.msg || 'Error loading benefits');
        this.loadingAvailable.set(false);
      }
    });
  }

  private getExistingBenefit(benefitId: string): any {
    return this.ecoBenefits.find(b => {
      const b_service_id = b.service_id?._id || b.service_id;
      return (b.benefit_id === benefitId) || (b_service_id === benefitId) || (b._id === benefitId);
    });
  }

  isAssigned(benefitId: string): boolean {
    return !!this.getExistingBenefit(benefitId);
  }

  /** Tier 2.4: AG Grid `(selectionChanged)` event replaces the row-header
   *  `<app-checkbox>` click handler. We pull the selected nodes from the API
   *  and toggle the `selected` flag on each row. */
  onAvailableSelectionChanged(event: any): void {
    const selectedIds = new Set(
      event.api.getSelectedRows().map((row: any) => row._id)
    );
    const all = this.availableBenefits();
    const updated = all.map((b: any) => ({
      ...b,
      selected: selectedIds.has(b._id)
    }));
    this.availableBenefits.set(updated);
  }

  onQuantityChange(benefit: any, newQuantity: number): void {
    benefit.quantity = Math.max(1, newQuantity);
    if (benefit.when_dates.length > benefit.quantity) {
      benefit.when_dates = benefit.when_dates.slice(0, benefit.quantity);
    }
    // Trigger reactivity: replace the array reference so any computed/listener
    // depending on this item re-evaluates.
    this.availableBenefits.set([...this.availableBenefits()]);
  }

  onDurationChange(benefit: any, newDuration: number): void {
    benefit.duration_months = newDuration;
    const maxDate = this.getMaxDate(benefit);
    benefit.when_dates = benefit.when_dates.filter((date: string) => new Date(date) <= maxDate);
    this.availableBenefits.set([...this.availableBenefits()]);
  }

  onDateSelectionChange(benefit: any, output: any): void {
    if (output.mode === 'any_time') {
      benefit.when_dates = [];
    } else {
      benefit.when_dates = [...output.dates];
    }
    this.availableBenefits.set([...this.availableBenefits()]);
  }

  getMaxDate(benefit: any): Date {
    const startDate = new Date();
    return new Date(startDate.getFullYear(), startDate.getMonth() + benefit.duration_months, startDate.getDate());
  }

  getMinDate(): Date {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }

  getAllDatesTooltip(dates: Date[]): string {
    if (!dates || dates.length === 0) return '';
    return dates.map(d => formatDate(d, 'MMM d y h:mm a', this.locale)).join(', ');
  }

  /** Selected subset — equivalent to the legacy `benefitsCollection.items`. */
  get selectedBenefits(): any[] {
    return this.availableBenefits().filter(b => b.selected);
  }

  save(): void {
    const selected = this.selectedBenefits.map(benefit => ({
      service_id: benefit.service_id?._id || benefit.service_id,
      service_name: benefit.service_id?.name || benefit.service_name,
      benefit_id: benefit._id,
      quantity: benefit.quantity,
      duration_months: benefit.duration_months,
      when_dates: benefit.when_dates,
      _id: benefit._id
    }));
    this.activeModal.close(selected);
  }
}
